/**
 * HelpdeskClientTicketMagic — Iter 98 Phase B.
 * Public route: /client/ticket/:token
 *
 * Token-scoped magic-link ticket view (no login required).
 * Allows the requester to see their ticket + reply.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, ArrowLeft, ShieldAlert, Loader2, Star } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function HelpdeskClientTicketMagic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reply, setReply] = useState("");
  const [posting, setP] = useState(false);
  const [csatBusy, setCsatBusy] = useState(false);
  const [csatFb, setCsatFb] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/client-portal/tickets/${token}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Link expired or invalid.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const send = async () => {
    if (!reply.trim()) return;
    setP(true);
    try {
      await axios.post(`${API}/public/client-portal/tickets/${token}/reply`,
                        { body: reply.replace(/\n/g, "<br>") });
      setReply("");
      toast.success("Reply sent");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to send"));
    } finally { setP(false); }
  };

  const submitCsat = async (rating) => {
    setCsatBusy(true);
    try {
      await axios.post(`${API}/public/client-portal/tickets/${token}/csat`,
                        { rating, feedback: csatFb || null });
      toast.success(`Thanks — ${rating}★`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to submit rating"));
    } finally { setCsatBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <div className="text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center p-4">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-lg p-6 text-center"
             data-testid="hd-magic-error">
          <ShieldAlert className="w-8 h-8 mx-auto text-red-500" />
          <h1 className="mt-3 font-bold">Link no longer valid</h1>
          <p className="text-sm text-slate-500 mt-2">{err}</p>
          <Link to="/client/portal" className="text-xs mt-4 inline-block underline text-slate-600">
            Go to client portal
          </Link>
        </div>
      </div>
    );
  }

  const t = data.ticket;
  const comments = data.comments || [];
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="hd-magic-ticket-page">
      <Toaster richColors position="top-center" />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <div className="min-w-0">
            <div className="font-bold text-sm">Support ticket</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Secure link view</div>
          </div>
          <div className="ml-auto text-[11px] text-slate-400 font-mono">{t.ticket_number}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6">
        <Link to="/client/portal" className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3 h-3" /> Client portal
        </Link>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
            {(t.status || "new").replace("_", " ")}
          </span>
          <h1 className="font-bold text-lg mt-2">{t.subject}</h1>
          <div className="text-[11px] text-slate-400 mt-1">
            Opened {new Date(t.created_at).toLocaleString()}
          </div>
          {t.description_html && (
            <div className="mt-4 text-sm text-slate-700"
                 dangerouslySetInnerHTML={{ __html: t.description_html }} />
          )}
        </div>

        <div className="mt-4 space-y-3" data-testid="hd-magic-conversation">
          {comments.map((c) => {
            const mine = c.author_kind === "client";
            return (
              <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                  mine ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"
                }`}>
                  <div className="text-[10px] opacity-70 mb-1 uppercase tracking-widest">
                    {mine ? "You" : (c.author_name || "Support")} · {new Date(c.created_at).toLocaleString()}
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: c.body }} />
                </div>
              </div>
            );
          })}
        </div>

        {["resolved", "closed"].includes(t.status) && (
          <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4" data-testid="hd-magic-csat">
            {t.csat_rating ? (
              <div className="text-sm text-slate-600">
                You rated this ticket <span className="font-semibold text-amber-600">{t.csat_rating}★</span>.
                {t.csat_feedback && <span className="ml-1 italic">"{t.csat_feedback}"</span>}
              </div>
            ) : (
              <div>
                <div className="text-sm font-semibold">How did we do?</div>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => submitCsat(n)} disabled={csatBusy}
                             data-testid={`hd-magic-csat-star-${n}`}
                             className="text-slate-300 hover:text-amber-500 transition-colors">
                      <Star className="w-6 h-6" />
                    </button>
                  ))}
                </div>
                <input value={csatFb} onChange={(e) => setCsatFb(e.target.value)}
                        placeholder="Tell us more (optional)"
                        data-testid="hd-magic-csat-feedback"
                        className="mt-3 w-full h-8 px-2 border border-slate-200 rounded text-xs bg-white focus:outline-none focus:border-slate-400" />
              </div>
            )}
          </div>
        )}

        {t.status !== "closed" && (
          <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
            <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
                       placeholder="Type your reply…"
                       data-testid="hd-magic-reply-body"
                       className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-500" />
            <div className="flex justify-end mt-2">
              <button onClick={send} disabled={posting || !reply.trim()}
                      data-testid="hd-magic-reply-submit"
                      className="text-sm font-semibold text-white rounded px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60 bg-slate-900">
                <Send className="w-3.5 h-3.5" />{posting ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
